import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import { ClinicalChecklist, CopingStrategies, CrisisTimeline, CSSRS, DistractingActivities, EatingCommitment, EatingSchedule, EnvironmentSafety, HelpContacts, MoodPlan, MyStoryMyPlanState, NumericDiagnostic, PreventingRelapse, ProfessionalHelpContacts, ReasonsForLiving, RiskFactors, SocialDistractions, SocialSupport, WarningSigns } from './avro';
import { StressSensitivityAssessment } from './avro/stressSensitivityAssessment';
import { YstMentalHealthConcerns } from '../ystMentalHealthConcerns';
import { YstOnboardingAvailability, YstRecurringAvailability } from '../ystScheduler';
// Composition templates that only have a single section
export var SingleSectionCompositionTemplates = {
    EATING_COMMITMENT: EatingCommitment,
    EATING_SCHEDULE: EatingSchedule,
    MYSTORYMYPLAN_CLINICAL: ClinicalChecklist,
    SCREENER_CSSRS: CSSRS,
    SCREENER_NUMERIC: NumericDiagnostic,
    STRESS_SENSITIVITY_ASSESSMENT: StressSensitivityAssessment,
    TEST: z.object({}).prefault({}),
    YST_MENTAL_HEALTH_CONCERNS: YstMentalHealthConcerns,
    YST_ONBOARDING_AVAILABILITY: YstOnboardingAvailability,
    YST_RECURRING_AVAILABILITY: YstRecurringAvailability
};
export var CompositionTemplates = {
    // single section composition templates
    EATING_COMMITMENT: z.object({
        EATING_COMMITMENT: SingleSectionCompositionTemplates.EATING_COMMITMENT
    }).strict().prefault({
        EATING_COMMITMENT: SingleSectionCompositionTemplates.EATING_COMMITMENT.parse(undefined)
    }),
    EATING_SCHEDULE: z.object({
        EATING_SCHEDULE: SingleSectionCompositionTemplates.EATING_SCHEDULE
    }).strict().prefault({
        EATING_SCHEDULE: SingleSectionCompositionTemplates.EATING_SCHEDULE.parse(undefined)
    }),
    TEST: z.object({
        TEST: SingleSectionCompositionTemplates.TEST
    }).strict().prefault({
        TEST: SingleSectionCompositionTemplates.TEST.parse(undefined)
    }),
    SCREENER_NUMERIC: z.object({
        SCREENER_NUMERIC: SingleSectionCompositionTemplates.SCREENER_NUMERIC
    }).strict().prefault({
        SCREENER_NUMERIC: SingleSectionCompositionTemplates.SCREENER_NUMERIC.parse(undefined)
    }),
    SCREENER_CSSRS: z.object({
        SCREENER_CSSRS: SingleSectionCompositionTemplates.SCREENER_CSSRS
    }).strict().prefault({
        SCREENER_CSSRS: SingleSectionCompositionTemplates.SCREENER_CSSRS.parse(undefined)
    }),
    STRESS_SENSITIVITY_ASSESSMENT: z.object({
        STRESS_SENSITIVITY_ASSESSMENT: SingleSectionCompositionTemplates.STRESS_SENSITIVITY_ASSESSMENT
    }).strict().prefault({
        STRESS_SENSITIVITY_ASSESSMENT: SingleSectionCompositionTemplates.STRESS_SENSITIVITY_ASSESSMENT.parse(undefined)
    }),
    MYSTORYMYPLAN_CLINICAL: z.object({
        MYSTORYMYPLAN_CLINICAL: SingleSectionCompositionTemplates.MYSTORYMYPLAN_CLINICAL
    }).strict().prefault({
        MYSTORYMYPLAN_CLINICAL: SingleSectionCompositionTemplates.MYSTORYMYPLAN_CLINICAL.parse(undefined)
    }),
    YST_MENTAL_HEALTH_CONCERNS: z.object({
        YST_MENTAL_HEALTH_CONCERNS: SingleSectionCompositionTemplates.YST_MENTAL_HEALTH_CONCERNS
    }).strict().prefault({
        YST_MENTAL_HEALTH_CONCERNS: SingleSectionCompositionTemplates.YST_MENTAL_HEALTH_CONCERNS.parse(undefined)
    }),
    YST_ONBOARDING_AVAILABILITY: z.object({
        YST_ONBOARDING_AVAILABILITY: YstOnboardingAvailability
    }).strict().prefault({
        YST_ONBOARDING_AVAILABILITY: SingleSectionCompositionTemplates.YST_ONBOARDING_AVAILABILITY.parse(undefined)
    }),
    YST_RECURRING_AVAILABILITY: z.object({
        YST_RECURRING_AVAILABILITY: YstRecurringAvailability
    }).strict().prefault({
        YST_RECURRING_AVAILABILITY: SingleSectionCompositionTemplates.YST_RECURRING_AVAILABILITY.parse(undefined)
    }),
    // multiple section composition templates
    BINGE_EATING_MY_PLAN: z.object({
        APP_STATE: MyStoryMyPlanState,
        DISTRACTING_ACTIVITIES: DistractingActivities,
        MOOD_PLAN: MoodPlan,
        SOCIAL_SUPPORT: SocialSupport,
        PREVENTING_RELAPSE: PreventingRelapse
    }).strict().prefault({
        APP_STATE: MyStoryMyPlanState.parse(undefined),
        DISTRACTING_ACTIVITIES: DistractingActivities.parse(undefined),
        MOOD_PLAN: MoodPlan.parse(undefined),
        SOCIAL_SUPPORT: SocialSupport.parse(undefined),
        PREVENTING_RELAPSE: PreventingRelapse.parse(undefined)
    }),
    MYSTORYMYPLAN: z.object({
        APP_STATE: MyStoryMyPlanState,
        CRISIS_TIMELINE: CrisisTimeline,
        WARNING_SIGNS: WarningSigns,
        COPING_STRATEGIES: CopingStrategies,
        // Missing sections identify older plans. Zod 4's optional() applies inner defaults.
        REASONS_FOR_LIVING: ReasonsForLiving.unwrap().optional(),
        SOCIAL_DISTRACTIONS: SocialDistractions,
        HELP_CONTACTS: HelpContacts,
        PROFESSIONAL_HELP_CONTACTS: ProfessionalHelpContacts,
        ENVIRONMENT_SAFETY: EnvironmentSafety,
        RISK_FACTORS: RiskFactors
    }).strict().prefault({
        APP_STATE: MyStoryMyPlanState.parse(undefined),
        CRISIS_TIMELINE: CrisisTimeline.parse(undefined),
        WARNING_SIGNS: WarningSigns.parse(undefined),
        COPING_STRATEGIES: CopingStrategies.parse(undefined),
        REASONS_FOR_LIVING: ReasonsForLiving.parse(undefined),
        SOCIAL_DISTRACTIONS: SocialDistractions.parse(undefined),
        HELP_CONTACTS: HelpContacts.parse(undefined),
        PROFESSIONAL_HELP_CONTACTS: ProfessionalHelpContacts.parse(undefined),
        ENVIRONMENT_SAFETY: EnvironmentSafety.parse(undefined),
        RISK_FACTORS: RiskFactors.parse(undefined)
    })
};
export function getBlankDocument(template, param) {
    var product = param.product;
    var document = CompositionTemplates[template].parse(undefined);
    if (product.slug == 'AVIVA_INPATIENT' && 'COPING_STRATEGIES' in document) {
        document.COPING_STRATEGIES = [
            {
                ID: uuid(),
                text: 'Do relaxation or mindfulness'
            }
        ];
    }
    return document;
}
export function getBlankSingleSectionDocument(template) {
    return SingleSectionCompositionTemplates[template].parse(undefined);
}
